import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  LineChart,
  Plus,
  Save,
  Trash2,
  Radio,
  Pause,
  Play,
  Send,
  Loader2,
  AlertTriangle,
  Sliders,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  adminBroadcastSignal,
  adminDeleteSignal,
  adminGetMarketConfig,
  adminListRecentSignals,
  adminSaveEngine,
  adminSaveFeeds,
  adminSaveInstruments,
} from "@/lib/admin-market.functions";

export const Route = createFileRoute("/_authenticated/admin/market")({
  component: MarketTab,
});

type Instrument = {
  symbol: string;
  name: string;
  category: "forex" | "metals" | "commodities" | "crypto" | "indices";
  enabled: boolean;
  tier: "free" | "pro" | "elite";
};
type FeedMap = Record<string, { enabled: boolean; label: string }>;
type Engine = {
  paused: boolean;
  conviction_threshold: number;
  refresh_seconds: number;
  agent_weights: Record<string, number>;
};

function MarketTab() {
  const getConfig = useServerFn(adminGetMarketConfig);
  const cfg = useQuery({ queryKey: ["admin-market-config"], queryFn: () => getConfig() });

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <div className="rounded-md border border-primary/40 bg-primary/10 p-1.5 text-primary">
            <LineChart className="h-4 w-4" />
          </div>
          <div>
            <h2 className="text-lg font-bold tracking-tight">Market Control</h2>
            <p className="text-xs text-muted-foreground">
              Manage instruments, data feeds, signal engine, and broadcast manual signals.
            </p>
          </div>
        </div>
        {cfg.data?.engine?.paused && (
          <Badge variant="destructive" className="gap-1">
            <Pause className="h-3 w-3" /> Engine paused
          </Badge>
        )}
      </div>

      {cfg.isLoading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading market config…
        </div>
      ) : cfg.error ? (
        <Card className="border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          {(cfg.error as Error).message}
        </Card>
      ) : (
        <Tabs defaultValue="instruments" className="space-y-4">
          <TabsList className="grid w-full max-w-2xl grid-cols-4">
            <TabsTrigger value="instruments">Instruments</TabsTrigger>
            <TabsTrigger value="feeds">Data Feeds</TabsTrigger>
            <TabsTrigger value="engine">Signal Engine</TabsTrigger>
            <TabsTrigger value="manual">Manual Signals</TabsTrigger>
          </TabsList>

          <TabsContent value="instruments">
            <InstrumentsPanel initial={cfg.data!.instruments as Instrument[]} />
          </TabsContent>
          <TabsContent value="feeds">
            <FeedsPanel initial={cfg.data!.feeds as FeedMap} />
          </TabsContent>
          <TabsContent value="engine">
            <EnginePanel initial={cfg.data!.engine as Engine} />
          </TabsContent>
          <TabsContent value="manual">
            <ManualSignalsPanel instruments={cfg.data!.instruments as Instrument[]} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

/* ---------- Instruments ---------- */
function InstrumentsPanel({ initial }: { initial: Instrument[] }) {
  const qc = useQueryClient();
  const save = useServerFn(adminSaveInstruments);
  const [items, setItems] = useState<Instrument[]>(initial);
  const [filter, setFilter] = useState<string>("all");

  useEffect(() => setItems(initial), [initial]);

  const mut = useMutation({
    mutationFn: (instruments: Instrument[]) => save({ data: { instruments } }),
    onSuccess: () => {
      toast.success("Instruments saved");
      qc.invalidateQueries({ queryKey: ["admin-market-config"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const filtered = items.filter((i) => filter === "all" || i.category === filter);
  const update = (idx: number, patch: Partial<Instrument>) => {
    const symbol = filtered[idx].symbol;
    setItems((all) => all.map((i) => (i.symbol === symbol ? { ...i, ...patch } : i)));
  };
  const remove = (symbol: string) => setItems((all) => all.filter((i) => i.symbol !== symbol));
  const add = () => {
    const newSym = `NEW${items.length + 1}`;
    setItems((all) => [
      ...all,
      { symbol: newSym, name: "New Instrument", category: "forex", enabled: false, tier: "free" },
    ]);
  };

  return (
    <Card className="border-border/60 bg-card/50 p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold">Tradeable Instruments</h3>
          <p className="text-xs text-muted-foreground">
            Toggle availability, assign minimum tier, edit display name.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="h-8 w-36 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              <SelectItem value="forex">Forex</SelectItem>
              <SelectItem value="metals">Metals</SelectItem>
              <SelectItem value="commodities">Commodities</SelectItem>
              <SelectItem value="crypto">Crypto</SelectItem>
              <SelectItem value="indices">Indices</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={add}>
            <Plus className="mr-1 h-3.5 w-3.5" /> Add
          </Button>
          <Button size="sm" onClick={() => mut.mutate(items)} disabled={mut.isPending}>
            {mut.isPending ? (
              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Save className="mr-1 h-3.5 w-3.5" />
            )}
            Save changes
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-md border border-border/60">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-28">Symbol</TableHead>
              <TableHead>Display name</TableHead>
              <TableHead className="w-32">Category</TableHead>
              <TableHead className="w-28">Min tier</TableHead>
              <TableHead className="w-24 text-center">Enabled</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((i, idx) => (
              <TableRow key={i.symbol}>
                <TableCell>
                  <Input
                    value={i.symbol}
                    onChange={(e) =>
                      update(idx, { symbol: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "") })
                    }
                    className="h-7 font-mono text-xs"
                  />
                </TableCell>
                <TableCell>
                  <Input
                    value={i.name}
                    onChange={(e) => update(idx, { name: e.target.value })}
                    className="h-7 text-xs"
                  />
                </TableCell>
                <TableCell>
                  <Select
                    value={i.category}
                    onValueChange={(v) => update(idx, { category: v as Instrument["category"] })}
                  >
                    <SelectTrigger className="h-7 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="forex">Forex</SelectItem>
                      <SelectItem value="metals">Metals</SelectItem>
                      <SelectItem value="commodities">Commodities</SelectItem>
                      <SelectItem value="crypto">Crypto</SelectItem>
                      <SelectItem value="indices">Indices</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <Select
                    value={i.tier}
                    onValueChange={(v) => update(idx, { tier: v as Instrument["tier"] })}
                  >
                    <SelectTrigger className="h-7 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="free">Free</SelectItem>
                      <SelectItem value="pro">Pro</SelectItem>
                      <SelectItem value="elite">Elite</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-center">
                  <Switch
                    checked={i.enabled}
                    onCheckedChange={(v) => update(idx, { enabled: v })}
                  />
                </TableCell>
                <TableCell>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 text-muted-foreground hover:text-destructive"
                    onClick={() => remove(i.symbol)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

/* ---------- Feeds ---------- */
function FeedsPanel({ initial }: { initial: FeedMap }) {
  const qc = useQueryClient();
  const save = useServerFn(adminSaveFeeds);
  const [feeds, setFeeds] = useState<FeedMap>(initial);
  useEffect(() => setFeeds(initial), [initial]);

  const mut = useMutation({
    mutationFn: (f: FeedMap) => save({ data: { feeds: f } }),
    onSuccess: () => {
      toast.success("Feed toggles saved");
      qc.invalidateQueries({ queryKey: ["admin-market-config"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Card className="border-border/60 bg-card/50 p-5">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <Radio className="h-4 w-4 text-primary" /> Data Feeds
          </h3>
          <p className="text-xs text-muted-foreground">
            Disable any feed globally if a provider is degraded or rate-limited.
          </p>
        </div>
        <Button size="sm" onClick={() => mut.mutate(feeds)} disabled={mut.isPending}>
          {mut.isPending ? (
            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Save className="mr-1 h-3.5 w-3.5" />
          )}
          Save
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {Object.entries(feeds).map(([key, f]) => (
          <div
            key={key}
            className="flex items-center justify-between rounded-md border border-border/60 bg-background/40 p-3"
          >
            <div className="min-w-0">
              <div className="text-sm font-medium">{f.label}</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                {key}
              </div>
            </div>
            <Switch
              checked={f.enabled}
              onCheckedChange={(v) =>
                setFeeds((cur) => ({ ...cur, [key]: { ...cur[key], enabled: v } }))
              }
            />
          </div>
        ))}
      </div>
    </Card>
  );
}

/* ---------- Engine ---------- */
function EnginePanel({ initial }: { initial: Engine }) {
  const qc = useQueryClient();
  const save = useServerFn(adminSaveEngine);
  const [engine, setEngine] = useState<Engine>(initial);
  useEffect(() => setEngine(initial), [initial]);

  const mut = useMutation({
    mutationFn: (e: Engine) => save({ data: { engine: e } }),
    onSuccess: () => {
      toast.success("Engine settings saved");
      qc.invalidateQueries({ queryKey: ["admin-market-config"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const setWeight = (agent: string, value: number) =>
    setEngine((e) => ({ ...e, agent_weights: { ...e.agent_weights, [agent]: value } }));

  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <Card className="border-border/60 bg-card/50 p-5 lg:col-span-1">
        <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold">
          <Sliders className="h-4 w-4 text-primary" /> Global Controls
        </h3>

        <div className="space-y-5">
          <div className="flex items-center justify-between rounded-md border border-border/60 bg-background/40 p-3">
            <div>
              <div className="text-sm font-medium">Pause Signal Engine</div>
              <div className="text-[11px] text-muted-foreground">
                Halts new signal generation across all users.
              </div>
            </div>
            <Switch
              checked={engine.paused}
              onCheckedChange={(v) => setEngine((e) => ({ ...e, paused: v }))}
            />
          </div>

          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <Label className="text-xs">Conviction threshold</Label>
              <span className="font-mono text-xs text-primary">
                {engine.conviction_threshold}
              </span>
            </div>
            <Slider
              value={[engine.conviction_threshold]}
              min={0}
              max={100}
              step={1}
              onValueChange={([v]) =>
                setEngine((e) => ({ ...e, conviction_threshold: v }))
              }
            />
            <p className="mt-1 text-[11px] text-muted-foreground">
              Minimum score required to publish a signal.
            </p>
          </div>

          <div>
            <Label className="text-xs">Refresh interval (seconds)</Label>
            <Input
              type="number"
              min={10}
              max={3600}
              value={engine.refresh_seconds}
              onChange={(e) =>
                setEngine((cur) => ({
                  ...cur,
                  refresh_seconds: Math.max(10, Math.min(3600, Number(e.target.value) || 60)),
                }))
              }
              className="mt-1 h-8"
            />
          </div>

          <Button onClick={() => mut.mutate(engine)} disabled={mut.isPending} className="w-full">
            {mut.isPending ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : engine.paused ? (
              <Play className="mr-1 h-4 w-4" />
            ) : (
              <Save className="mr-1 h-4 w-4" />
            )}
            Save engine
          </Button>
        </div>
      </Card>

      <Card className="border-border/60 bg-card/50 p-5 lg:col-span-2">
        <h3 className="mb-4 text-sm font-semibold">Agent Weight Matrix</h3>
        <p className="mb-4 text-xs text-muted-foreground">
          Tune the influence of each AI council member on the final conviction score (0 = mute, 1 =
          normal, 3 = max).
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          {Object.entries(engine.agent_weights).map(([agent, weight]) => (
            <div key={agent} className="rounded-md border border-border/60 bg-background/40 p-3">
              <div className="mb-1.5 flex items-center justify-between">
                <span className="text-xs font-medium capitalize">
                  {agent.replace(/_/g, " ")}
                </span>
                <span className="font-mono text-xs text-primary">{weight.toFixed(2)}×</span>
              </div>
              <Slider
                value={[weight]}
                min={0}
                max={3}
                step={0.05}
                onValueChange={([v]) => setWeight(agent, v)}
              />
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

/* ---------- Manual Signals ---------- */
function ManualSignalsPanel({ instruments }: { instruments: Instrument[] }) {
  const qc = useQueryClient();
  const broadcast = useServerFn(adminBroadcastSignal);
  const list = useServerFn(adminListRecentSignals);
  const del = useServerFn(adminDeleteSignal);

  const enabled = useMemo(() => instruments.filter((i) => i.enabled), [instruments]);
  const [form, setForm] = useState({
    pair: enabled[0]?.symbol ?? "EURUSD",
    action: "BUY" as "BUY" | "SELL" | "HOLD",
    score: 80,
    entry: "" as string,
    stop_loss: "" as string,
    take_profit: "" as string,
    note: "",
  });

  const signals = useQuery({
    queryKey: ["admin-recent-signals"],
    queryFn: () => list(),
  });

  const sendMut = useMutation({
    mutationFn: () =>
      broadcast({
        data: {
          pair: form.pair,
          action: form.action,
          score: form.score,
          entry: form.entry ? Number(form.entry) : null,
          stop_loss: form.stop_loss ? Number(form.stop_loss) : null,
          take_profit: form.take_profit ? Number(form.take_profit) : null,
          note: form.note || undefined,
        },
      }),
    onSuccess: () => {
      toast.success("Signal broadcasted");
      setForm((f) => ({ ...f, note: "" }));
      qc.invalidateQueries({ queryKey: ["admin-recent-signals"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => del({ data: { id } }),
    onSuccess: () => {
      toast.success("Signal deleted");
      qc.invalidateQueries({ queryKey: ["admin-recent-signals"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="grid gap-4 lg:grid-cols-5">
      <Card className="border-border/60 bg-card/50 p-5 lg:col-span-2">
        <h3 className="mb-1 flex items-center gap-2 text-sm font-semibold">
          <Send className="h-4 w-4 text-primary" /> Broadcast Manual Signal
        </h3>
        <p className="mb-4 text-xs text-muted-foreground">
          Pushes a signal record visible to all subscribers of this pair.
        </p>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Pair</Label>
              <Select
                value={form.pair}
                onValueChange={(v) => setForm((f) => ({ ...f, pair: v }))}
              >
                <SelectTrigger className="mt-1 h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {enabled.map((i) => (
                    <SelectItem key={i.symbol} value={i.symbol}>
                      {i.symbol}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Action</Label>
              <Select
                value={form.action}
                onValueChange={(v) =>
                  setForm((f) => ({ ...f, action: v as typeof form.action }))
                }
              >
                <SelectTrigger className="mt-1 h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="BUY">BUY</SelectItem>
                  <SelectItem value="SELL">SELL</SelectItem>
                  <SelectItem value="HOLD">HOLD</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between">
              <Label className="text-xs">Conviction score</Label>
              <span className="font-mono text-xs text-primary">{form.score}</span>
            </div>
            <Slider
              value={[form.score]}
              min={0}
              max={100}
              step={1}
              onValueChange={([v]) => setForm((f) => ({ ...f, score: v }))}
            />
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">Entry</Label>
              <Input
                value={form.entry}
                onChange={(e) => setForm((f) => ({ ...f, entry: e.target.value }))}
                className="mt-1 h-8 text-xs"
                placeholder="—"
              />
            </div>
            <div>
              <Label className="text-xs">Stop</Label>
              <Input
                value={form.stop_loss}
                onChange={(e) => setForm((f) => ({ ...f, stop_loss: e.target.value }))}
                className="mt-1 h-8 text-xs"
                placeholder="—"
              />
            </div>
            <div>
              <Label className="text-xs">Target</Label>
              <Input
                value={form.take_profit}
                onChange={(e) => setForm((f) => ({ ...f, take_profit: e.target.value }))}
                className="mt-1 h-8 text-xs"
                placeholder="—"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs">Internal note</Label>
            <Textarea
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              className="mt-1 text-xs"
              rows={2}
              placeholder="Optional reasoning logged with the signal payload"
            />
          </div>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button className="w-full" disabled={sendMut.isPending}>
                {sendMut.isPending ? (
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                ) : (
                  <Send className="mr-1 h-4 w-4" />
                )}
                Broadcast signal
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-warn" />
                  Confirm manual broadcast
                </AlertDialogTitle>
                <AlertDialogDescription>
                  This will publish a <b>{form.action}</b> signal on{" "}
                  <b>{form.pair}</b> at conviction <b>{form.score}</b>. Proceed?
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => sendMut.mutate()}>
                  Broadcast
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </Card>

      <Card className="border-border/60 bg-card/50 p-5 lg:col-span-3">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold">Recent Signals</h3>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => signals.refetch()}
            className="text-xs"
          >
            Refresh
          </Button>
        </div>
        {signals.isLoading ? (
          <div className="flex items-center justify-center py-8 text-xs text-muted-foreground">
            <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="overflow-x-auto rounded-md border border-border/60">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-24">Pair</TableHead>
                  <TableHead className="w-20">Action</TableHead>
                  <TableHead className="w-16">Score</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-20">Source</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {(signals.data?.signals ?? []).map((s) => {
                  const isManual =
                    s.payload && typeof s.payload === "object" && (s.payload as { manual?: boolean }).manual;
                  return (
                    <TableRow key={s.id}>
                      <TableCell className="font-mono text-xs">{s.pair}</TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            s.action === "BUY"
                              ? "default"
                              : s.action === "SELL"
                                ? "destructive"
                                : "secondary"
                          }
                          className="text-[10px]"
                        >
                          {s.action}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{Number(s.score).toFixed(0)}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {new Date(s.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Badge variant={isManual ? "outline" : "secondary"} className="text-[10px]">
                          {isManual ? "Manual" : "Engine"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          onClick={() => deleteMut.mutate(s.id)}
                          disabled={deleteMut.isPending}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {(signals.data?.signals ?? []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="py-8 text-center text-xs text-muted-foreground">
                      No signals yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
